import React, { useState } from 'react';
import CustomFAQsTabContent from './CustomFAQsTabContent';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { PlusIcon } from '@heroicons/react/20/solid';
import { SectionHeader } from '@/components/ui/section-header';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { EmptyState } from '../../../../components/ui/empty-state';

interface CustomFAQ {
  id: string;
  question: string;
  answer: string;
  required: boolean;
}

interface CustomFAQsTabProps {
  customFAQs: CustomFAQ[];
  setCustomFAQs: (faqs: CustomFAQ[]) => void;
  showSectionHeader?: boolean;
  handleFieldFocus: (fieldName: string) => void;
  isFieldInvalid: (fieldName: string) => boolean;
  setInvalidFields?: (fields: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
}

function CustomFAQsTab(props: CustomFAQsTabProps) {
  const { customFAQs, setCustomFAQs, handleFieldFocus, isFieldInvalid, setInvalidFields } = props;
  const [faqLimitReached, setFaqLimitReached] = useState(false);

  const handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = parseInt(event.target.dataset.index || '0');
    const value = event.target.value;

    const newcustomFAQs = [...customFAQs];
    const newCustomInput = newcustomFAQs[index];
    newCustomInput.question = value;
    setCustomFAQs(newcustomFAQs);
  };

  const handleAnswerChange = (options: string, index: number) => {
    const newcustomFAQs = [...customFAQs];
    const newCustomInput = newcustomFAQs[index];
    newCustomInput.answer = options;
    setCustomFAQs(newcustomFAQs);
  };

  const createNewCustomInput = () => {
    if (customFAQs.length < 4) {
      const newcustomFAQs = [...customFAQs];
      newcustomFAQs.push({
        question: '',
        answer: '',
        required: false,
        id: Math.random().toString(36).substring(2, 9), // generates a random id
      });
      setCustomFAQs(newcustomFAQs);
    } else {
      setFaqLimitReached(true); // Set the state variable to indicate the limit has been reached
    }
  };

  const removeCustomFAQs = (index: number) => {
    const newcustomFAQs = [...customFAQs];
    newcustomFAQs.splice(index, 1);
    setCustomFAQs(newcustomFAQs);
    setFaqLimitReached(false); // Reset to false when an FAQ is removed

    // Clean up validation errors for this FAQ
    if (setInvalidFields) {
      setInvalidFields((prev) => {
        const newInvalidFields = new Set(prev);

        // Remove validation errors for all FAQ fields
        // We need to remove errors for all indices >= the removed index
        // and shift down the indices for FAQs that come after
        Array.from(newInvalidFields).forEach((field) => {
          if (field.startsWith('faqs-faq-')) {
            // Extract the index from field name (e.g., 'faqs-faq-question-2' -> 2)
            const match = field.match(/faqs-faq-(?:question|answer)-(\d+)/);
            if (match) {
              const fieldIndex = parseInt(match[1]);

              // If this is the removed FAQ, delete its validation errors
              if (fieldIndex === index) {
                newInvalidFields.delete(field);
              }
              // If this is an FAQ after the removed one, update its index
              else if (fieldIndex > index) {
                newInvalidFields.delete(field);
                // Add the field back with updated index (shifted down by 1)
                const fieldType = field.includes('question') ? 'question' : 'answer';
                newInvalidFields.add(`faqs-faq-${fieldType}-${fieldIndex - 1}`);
              }
            }
          }
        });

        // If no FAQs remain, remove all FAQ-related validation errors
        if (newcustomFAQs.length === 0) {
          Array.from(newInvalidFields).forEach((field) => {
            if (field.startsWith('faqs-')) {
              newInvalidFields.delete(field);
            }
          });
        }

        return newInvalidFields;
      });
    }
  };

  const onDragEnd = (result: any) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const newcustomFAQs = [...customFAQs];
    const newCustomInput = newcustomFAQs[source.index];
    newcustomFAQs.splice(source.index, 1);
    newcustomFAQs.splice(destination.index, 0, newCustomInput);
    setCustomFAQs(newcustomFAQs);
  };

  return (
    <div className={`space-y-3 ${props.showSectionHeader === false ? '' : 'pr-2'}`}>
      {props.showSectionHeader !== false && <SectionHeader title="FAQs Configuration" />}
      <div className="space-y-3">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <div className="space-y-4">
                  {customFAQs.length === 0 ? (
                    <EmptyState
                      icon={<QuestionMarkCircleIcon className="h-6 w-6 text-ink-muted" />}
                      title="No FAQs added"
                      description="Add frequently asked questions to help donors understand your fundraiser better."
                      actionLabel="Add FAQ"
                      onAction={createNewCustomInput}
                    />
                  ) : (
                    customFAQs.map((customInput, index) => (
                      <div key={customInput.id}>
                        <h3 className="text-base font-medium text-ink-secondary">
                          FAQ #{index + 1}
                        </h3>
                        <CustomFAQsTabContent
                          id={customInput.id}
                          customFAQs={customInput}
                          index={index}
                          handleQuestionChange={handleQuestionChange}
                          handleAnswerChange={handleAnswerChange}
                          removeCustomFAQs={removeCustomFAQs}
                          handleFieldFocus={handleFieldFocus}
                          isFieldInvalid={isFieldInvalid}
                        />
                      </div>
                    ))
                  )}
                </div>

                {provided.placeholder}
                {customFAQs.length > 0 && (
                  <div className="mt-1 flex w-full justify-end">
                    {faqLimitReached ? (
                      <span className="text-sm text-red-500">
                        You can only create up to four FAQs.
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={createNewCustomInput}
                        className="flex items-center space-x-1 text-ink-secondary hover:text-ink"
                      >
                        <PlusIcon className="h-4 w-4" />
                        <span className="text-sm">Add FAQ</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}

export default CustomFAQsTab;
